// -----------------------------------------------------------------------------
// Plans (v0.6.0 Part 6) — boards A4 Plan view, A5.2 Place / move pin, A6 Plan column + A6.7 Upload plan.
//   * PlanCanvas: the component's current plan page (pdf.js, core/pdfPlan.ts) with numbered pins coloured by status
//     (Open orange, Critical red, Closed green faded; voided hidden). Pins sit at x / y = 0–1 across / down the page,
//     so they stay put when a new plan version (same layout) is uploaded.
//   * PlanToolbar: "📄 file · V01", legend, (i).
//   * PlaceStatus / PlacePinModal (A5.2): click the plan to place the pin, drag to move it; Remove pin / Cancel / Save pin.
//   * PlanUploadModal (A6.7): PDF ≤ 20 MB → page pick when it has several → preview + ⟲ ⟳ (v0.7.1) → "Upload as V02".
//     Older versions are kept.
// v0.7.0: A5.2 shows the pin's gridline reading ("📍 Pin at gridline C/17", grid.ts pinReading); Save pin also fills
// the issue's Gridline field with it (still editable). "Adjust grid" (Project setup) opens the A6.4 Gridline setup.
// Data: issues_pins (+ grid, can_setup — 0016), issues_pin_set, issues_update, plans_get, plan_add (0015_files.sql).
// -----------------------------------------------------------------------------
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { rpc } from "../../../core/api/backend";
import { useRpc } from "../../../core/api/useRpc";
import { formatBytes, PLAN_MAX, uploadPlan } from "../../../core/files";
import { nextPlanVersion, openPdf, pinFromClick, turnBy, type OpenPdf } from "../../../core/pdfPlan";
import { ErrorBox, InfoIcon, Loading, Modal, toast } from "../../../core/ui/kit";
import { formatDate } from "../../../core/format";
import { useFileUrl } from "./Snapshots";
import type { Grid, PlanFile, PlanPin as Pin } from "../../../core/api/types";
import { pinReading } from "../grid";
import { GridSetupModal } from "./GridSetup";
import { useWheelZoom } from "../../../core/wheelZoom";
import { Icon } from "../../../core/ui/icons";

export const PIN_COLOUR: Record<string, string> = { Open: "#EF9F27", Critical: "#B9481F", Closed: "#639922" };
export const LEGEND_TIP = ["Voided issues are hidden.", "Pin numbers = issue number.", "Pins follow the status and discipline filters above."];

/** Opened PDFs by object name (a plan is drawn in several places). */
const docs = new Map<string, Promise<OpenPdf>>();
export function useOpenPdf(path: string | null | undefined) {
  const url = useFileUrl(path);
  const [doc, setDoc] = useState<OpenPdf | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    if (!path || !url) return;
    let live = true;
    if (!docs.has(path)) docs.set(path, openPdf(url).catch((e) => { docs.delete(path); throw e; }));
    docs.get(path)!.then((d) => live && setDoc(d)).catch((e) => live && setErr(`The plan could not be opened: ${(e as Error).message}`));
    return () => { live = false; };
  }, [path, url]);
  return { doc, err };
}

export function PlanToolbar({ name, version, extra, tip = LEGEND_TIP }: { name: string; version: string; extra?: ReactNode; tip?: string[] }) {
  return (
    <div className="plan-toolbar">
      {extra}
      <span className="muted tiny ellipsis"><Icon name="picture_as_pdf" /> {name} · {version}</span>
      <span className="grow" />
      {Object.entries(PIN_COLOUR).map(([k, c]) => <span key={k} className="legend"><span className={`dot${k === "Closed" ? " faded" : ""}`} style={{ background: c }} />{k}</span>)}
      <InfoIcon tip={tip} />
    </div>
  );
}

/**
 * The plan page with pins. `focus` = the one issue that stands out (others faded, A5.2);
 * `onPlace` = clicks / drags place the focus pin (A5.2); `onPin` / `onPinOpen` = click / double-click a pin (A4).
 * v0.7.1: `rotation` = quarter turns chosen at upload; Ctrl + wheel zooms the plan around the pointer (1–8×, the holder
 * scrolls), a plain wheel scrolls (round 25 #12). Each drawing goes to its own off-screen canvas and is copied in when done,
 * so a resize while drawing can't leave the plan stuck at "Loading" (v070-035) and zooming doesn't flash white.
 */
export function PlanCanvas({ path, page, rotation = 0, pins, focus, place, onPlace, onPin, onPinOpen, active, grid }: {
  path: string; page: number; rotation?: number; pins: Pin[]; focus?: string; place?: { x: number; y: number } | null;
  onPlace?: (p: { x: number; y: number }) => void; onPin?: (p: Pin) => void; onPinOpen?: (p: Pin) => void; active?: string | null;
  /** v0.9.1 (round 32 #13): the saved gridlines (A6.4) drawn over the plan in Place pin — same 0–1 space as the pins */
  grid?: Grid | null;
}) {
  const { doc, err } = useOpenPdf(path);
  const wrap = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const pageEl = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });           // room for the plan: wrap width × scroll-box height
  const [ratio, setRatio] = useState<number | null>(null);  // page height / width, known after the first draw
  const [drawn, setDrawn] = useState(false);
  const [hover, setHover] = useState<Pin | null>(null);
  const [zoom, setZoom] = useState(1);
  const anchor = useRef<{ fx: number; fy: number; cx: number; cy: number } | null>(null);
  const dragging = useRef(false);
  useEffect(() => {
    const el = wrap.current; if (!el) return;
    const holder = el.parentElement!;
    const measure = () => setBox({ w: Math.floor(holder.clientWidth), h: Math.floor(holder.clientHeight) });
    const ro = new ResizeObserver(measure);
    ro.observe(holder); measure();
    return () => ro.disconnect();
  }, []);
  // the whole page fits the box (width, and height when the box has one) at zoom 1
  const fitW = ratio && box.h > 80 ? Math.max(240, Math.min(box.w, Math.floor((box.h - 4) / ratio))) : box.w;
  const width = Math.round(fitW * zoom);
  // show the new size at once (stretched), then draw it sharp after a short pause
  useLayoutEffect(() => {
    const c = canvas.current; if (!c || !ratio || width < 50) return;
    c.style.width = `${width}px`; c.style.height = `${Math.round(width * ratio)}px`;
    const a = anchor.current, holder = wrap.current?.parentElement, r = pageEl.current?.getBoundingClientRect();
    if (a && holder && r) { holder.scrollLeft += r.left + a.fx * r.width - a.cx; holder.scrollTop += r.top + a.fy * r.height - a.cy; anchor.current = null; }
  }, [width, ratio]);
  const seq = useRef(0);
  useEffect(() => {
    if (!doc || !canvas.current || width < 50) return;
    const my = ++seq.current;
    const t = setTimeout(() => {
      const off = document.createElement("canvas");
      doc.render(page, off, width, 1, rotation).then((r) => {
        const c = canvas.current;
        if (my !== seq.current || !c) return;                 // a newer drawing is on its way
        c.width = off.width; c.height = off.height; c.style.width = off.style.width; c.style.height = off.style.height;
        c.getContext("2d")!.drawImage(off, 0, 0);
        setDrawn(true); setRatio((old) => (old && Math.abs(old - r) < 0.001 ? old : r));
      }).catch((e) => { if (my === seq.current) console.warn("plan drawing failed", e); });
    }, drawn ? 150 : 0);
    return () => clearTimeout(t);
  }, [doc, page, rotation, width]); // eslint-disable-line react-hooks/exhaustive-deps
  useWheelZoom(wrap, (k, cx, cy) => {
    const r = pageEl.current?.getBoundingClientRect(); if (!r || !r.width) return;
    anchor.current = { fx: (cx - r.left) / r.width, fy: (cy - r.top) / r.height, cx, cy };
    setZoom((z) => Math.max(1, Math.min(8, Math.round(z * k * 100) / 100)));
  });
  const at = (e: { clientX: number; clientY: number }) => canvas.current && pinFromClick(e.clientX, e.clientY, canvas.current.getBoundingClientRect());
  const down = (e: RPointerEvent) => { if (!onPlace) return; const p = at(e); if (p) { dragging.current = true; (e.target as Element).setPointerCapture?.(e.pointerId); onPlace(p); } };
  const move = (e: RPointerEvent) => { if (onPlace && dragging.current) { const p = at(e); if (p) onPlace(p); } };
  const shown = pins.filter((p) => p.status !== "Voided").map((p) => (p.code === focus && place !== undefined ? { ...p, x: place?.x ?? null, y: place?.y ?? null } : p));
  return (
    <div className={`plan-wrap${onPlace ? " placing" : ""}`} ref={wrap} data-testid="plan-canvas" data-drawn={drawn ? "1" : "0"} data-zoom={zoom}>
      {err && <ErrorBox message={err} />}
      {!drawn && !err && <Loading />}
      {zoom > 1 && <button type="button" className="btn plan-zoom" title="Back to the whole plan" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}% · Fit</button>}
      <div className="plan-page" ref={pageEl} onPointerDown={down} onPointerMove={move} onPointerUp={() => { dragging.current = false; }}>
        <canvas ref={canvas} aria-label="Plan" />
        {drawn && grid && grid.lines.length > 0 && <GridOverlay grid={grid} />}
        {drawn && shown.filter((p) => p.x !== null && p.y !== null).map((p) => (
          <button key={p.id} type="button" data-pin={p.code}
            className={`pin${focus && p.code !== focus ? " dim" : ""}${p.code === focus ? " focus" : ""}${p.status === "Closed" ? " closed" : ""}${active === p.code ? " active" : ""}`}
            style={{ left: `${Number(p.x) * 100}%`, top: `${Number(p.y) * 100}%`, background: PIN_COLOUR[p.status] }}
            aria-label={`${p.code} pin`} tabIndex={onPin ? 0 : -1}
            onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)}
            onClick={(e) => { if (onPin) { e.stopPropagation(); onPin(p); } }} onDoubleClick={() => onPinOpen?.(p)}>{p.seq}</button>))}
        {drawn && hover && !onPlace && hover.x !== null && (
          <div className="pin-hover" style={{ left: `${Number(hover.x) * 100}%`, top: `${Number(hover.y) * 100}%` }}>
            <b className="code">{hover.code}</b> · {hover.status}<p>{hover.description}</p></div>)}
      </div>
    </div>
  );
}

/** v0.9.1 (round 32 #13, Kyle v0.9.0 acceptance): the gridlines saved in Gridline setup, drawn over the plan (thin green — v0.9.2 #9 —
 *  lines, label at the start of each line; amber = to check). Scales with the plan — the SVG uses the 0–1 page space. */
export function GridOverlay({ grid }: { grid: Grid }) {
  return (
    <svg className="grid-overlay" data-testid="grid-overlay" viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
      {grid.lines.map((l) => {
        const amber = !l.label || l.check;
        return <g key={l.id} data-grid-line={l.label ?? "?"}>
          <line x1={l.x1 * 1000} y1={l.y1 * 1000} x2={l.x2 * 1000} y2={l.y2 * 1000} className={amber ? "amber" : ""} vectorEffect="non-scaling-stroke" />
        </g>;
      })}
    </svg>
  );
}

/** A5.2 Place / move pin (pop-up from the 📍 on the Gridline card). */
export const PIN_TIP = ["The Gridline field is filled with this reading.", "You can still edit it."];
export const PLACE_TIP = ["Click the plan to place the pin, drag to move it.", "Other issues are faded.", "Ctrl + mouse wheel zooms the plan; drag the corner to resize the window."];
export function PlacePinModal({ code, prefix, issue, onClose, onSaved }: { code: string; prefix: string; issue: { id: string; code: string; gridline?: string }; onClose: () => void; onSaved: () => void }) {
  const { data, error, reload } = useRpc("issues_pins", { code, prefix });
  const [gridOpen, setGridOpen] = useState(false);
  const mine = data?.pins.find((p) => p.code === issue.code);
  const [place, setPlace] = useState<{ x: number; y: number } | null | undefined>(undefined);   // undefined = unchanged
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const cur = place === undefined ? (mine && mine.x !== null ? { x: Number(mine.x), y: Number(mine.y) } : null) : place;
  const reading = cur && data?.grid ? pinReading(data.grid.lines, data.grid.zones, cur.x, cur.y, data.grid.ratio ?? 0.707) : null;
  const save = async (p: { x: number; y: number } | null) => {
    setBusy(true);
    try {
      await rpc("issues_pin_set", { id: issue.id, x: p?.x ?? null, y: p?.y ?? null });
      if (p && reading && reading !== issue.gridline) await rpc("issues_update", { id: issue.id, gridline: reading });   // A5.2 tip: the Gridline field is filled
      toast(p ? (reading ? `Pin saved · Gridline ${reading}` : "Pin saved") : "Pin removed"); onSaved();
    }
    catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  return (
    <Modal title={`Place pin · ${issue.code}`} onClose={onClose} className="pin-modal">
      {(error || err) && <ErrorBox message={(error || err)!} />}
      {!data ? (!error && <Loading />) : !data.plan ? (
        <p className="muted pad" data-testid="no-plan">No plan for this component yet. Upload one in Project setup › Components › Plan.</p>
      ) : <>
        <PlanToolbar name={data.plan.name} version={data.plan.version} extra={<InfoIcon tip={PLACE_TIP} />} tip={LEGEND_TIP.slice(0, 2)} />
        <div className="pin-modal-plan">
          <PlanCanvas path={data.plan.path} page={data.plan.page} rotation={data.plan.rotation} pins={data.pins} focus={issue.code} place={cur} onPlace={data.can_edit ? setPlace : undefined} grid={data.grid} />
        </div>
        <div className="row between pin-modal-foot">
          <p className="muted small row gap6" data-testid="pin-state"><Icon name="place" /> {!cur ? "Click the plan to place the pin" : reading ? <>Pin at gridline <b className="ink" data-testid="pin-reading">{reading}</b> <InfoIcon tip={PIN_TIP} /></>
            : data.grid ? "Pin placed — outside the gridlines" : "Pin placed — drag it to adjust (no gridlines set up yet)"}</p>
          <div className="row gap12">
            {mine?.x !== null && mine?.x !== undefined && <button className="btn" disabled={busy} onClick={() => save(null)}>Remove pin</button>}
            {data.can_setup && <button className="btn" onClick={() => setGridOpen(true)}>Adjust grid</button>}
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn primary" disabled={busy || !cur || place === undefined} onClick={() => save(cur)}>Save pin</button>
          </div>
        </div>
      </>}
      {gridOpen && <GridSetupModal code={code} prefix={prefix} onClose={() => setGridOpen(false)} onSaved={() => { setGridOpen(false); reload(); }} />}
    </Modal>
  );
}

/** A6.7 Upload plan (from the A6 Plan column). v0.7.1 (round 25 #10): a preview of the chosen page with ⟲ ⟳ to turn it
 *  before uploading; the turn is stored with the plan (plans.rotation, 0017) and every view draws it turned. */
export const UPLOAD_TIP = ["PDF only, up to 20 MB.", "Each upload becomes the next plan version (V00, V01 …); older versions are kept.",
  "Pins and gridlines stay; check them on the new plan in Gridline setup.", "A PDF with several pages: pick the page that holds the plan.",
  "Check the preview; ⟲ ⟳ turn the plan before uploading (pins and gridlines follow the turned plan)."];
export function PlanUploadModal({ code, prefix, name, projectId, current, onClose, onDone }: {
  code: string; prefix: string; name: string; projectId: string; current: PlanFile | null; onClose: () => void; onDone: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [pdf, setPdf] = useState<OpenPdf | null>(null);
  const [page, setPage] = useState(1);
  const [turn, setTurn] = useState<0 | 90 | 180 | 270>(0);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const preview = useRef<HTMLCanvasElement>(null);
  const holder = useRef<HTMLDivElement>(null);
  const pages = pdf?.pages ?? 0;
  const next = nextPlanVersion(current?.version);
  useEffect(() => () => pdf?.close(), [pdf]);
  const pick = async (f: File | null | undefined) => {
    setErr(null); setFile(null); setPdf(null); setPage(1); setTurn(0);
    if (!f) return;
    if (f.type !== "application/pdf" && !/\.pdf$/i.test(f.name)) return setErr("Plans must be PDF files");
    if (f.size > PLAN_MAX) return setErr(`This PDF is ${formatBytes(f.size)} — plans must be 20 MB or less`);
    setFile(f);
    try { setPdf(await openPdf(f)); }
    catch { setFile(null); setErr("This PDF could not be read"); }
  };
  // the preview fits a 500 × 250 box whichever way the page is turned
  useEffect(() => {
    if (!pdf || !preview.current || !holder.current) return;
    let live = true;
    const off = document.createElement("canvas");
    const boxW = holder.current.clientWidth - 16, boxH = 234;
    pdf.render(page, off, 400, 1, turn).then((r) => {
      if (!live || !preview.current) return;
      const w = Math.min(boxW, boxH / r), c = preview.current;
      c.width = off.width; c.height = off.height; c.style.width = `${Math.round(w)}px`; c.style.height = `${Math.round(w * r)}px`;
      c.getContext("2d")!.drawImage(off, 0, 0);
    }).catch(() => {});
    return () => { live = false; };
  }, [pdf, page, turn]);
  const upload = async () => {
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const up = await uploadPlan(projectId, file);
      const r = await rpc("plan_add", { code, prefix, page, pages: Math.max(1, pages), rotation: turn, ...up });
      toast(`Plan ${r.version} uploaded`); onDone();
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  return (
    <Modal title={`${prefix} ${name} · plan`} onClose={onClose} className="plan-upload" info={UPLOAD_TIP}>
      {current && <div className="field-card plan-current" data-plan-current={`${prefix}:${current.version}`}>
        <div><p className="k">Current plan</p><p className="v">{current.version} · {current.name}</p></div>
        <span className="muted small">{formatDate(current.created_at)}{current.created_by && <> · {current.created_by}</>}</span></div>}
      {err && <ErrorBox message={err} />}
      <div className={`plan-drop${over ? " over" : ""}`} role="button" tabIndex={0} data-testid="plan-drop"
        onClick={() => input.current?.click()} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); input.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files[0]); }}>
        <Icon name="file_upload" /> Drag a PDF here or <span className="accent-text">Browse…</span>
        <input ref={input} type="file" accept="application/pdf,.pdf" hidden aria-label="Plan PDF" onChange={(e) => pick(e.target.files?.[0])} />
      </div>
      {file && (
        <div className="plan-new" data-plan-new={next}>
          <span className="ellipsis grow">{file.name} <span className="muted">· {formatBytes(file.size)}</span></span>
          {pages > 1 ? <label className="muted small row gap6">Page
            <select aria-label="Page" value={page} onChange={(e) => setPage(Number(e.target.value))}>{Array.from({ length: pages }, (_, k) => <option key={k} value={k + 1}>{k + 1}</option>)}</select> of {pages}</label>
            : <span className="muted small">{pages ? "1 page" : "Reading…"}</span>}
          <b>→ {next}</b>
        </div>)}
      {pdf && (
        <div className="plan-preview" data-plan-preview={turn}>
          <div className="row between muted small"><span>Preview · page {page}{turn ? ` · turned ${turn}°` : ""}</span>
            <span className="row gap8">
              <button type="button" className="btn icon" title="Turn left" aria-label="Turn plan left" onClick={() => setTurn((t) => turnBy(t, -1))}><Icon name="rotate_left" /></button>
              <button type="button" className="btn icon" title="Turn right" aria-label="Turn plan right" onClick={() => setTurn((t) => turnBy(t, 1))}><Icon name="rotate_right" /></button>
            </span></div>
          <div className="plan-preview-box" ref={holder}><canvas ref={preview} aria-label="Plan preview" /></div>
        </div>)}
      <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={!file || !pages || busy} onClick={upload}>{busy ? "Uploading…" : `Upload as ${next}`}</button></div>
    </Modal>
  );
}
